import { useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useProceedingEdit } from '@/module/Author/Proceeding/model/ProceedingQuery';
import {
  ProceedingSchema,
  defaultProceedingValues,
  proceedingSchema,
} from '@/module/Author/Proceeding/model/ProceedingSchema';
import { defaultIdentifiers } from '@/module/Author/Publication/model/PublicationTypes';

import { useAppNotification } from '@feature/utils';

import { ProceedingForm } from './ProceedingForm';
import { Proceeding } from './model/ProceedingTypes';

interface ProceedingEditProps {
  proceeding: Proceeding;
}

const convert = (proceeding: Proceeding): ProceedingSchema => ({
  ...proceeding,
  identifiers: proceeding.identifiers ?? defaultIdentifiers,
});

export function ProceedingEdit(props: ProceedingEditProps) {
  const { proceeding } = props;
  const { t } = useTranslation();
  const { mutate, isPending, isError, isSuccess } = useProceedingEdit(proceeding.id);
  const notification = useAppNotification();

  const onSubmit = (values: ProceedingSchema) => {
    mutate(values, {
      onError: notification.error,
      onSuccess: notification.success,
    });
  };

  const values = useMemo(() => convert(proceeding), [proceeding]);
  const navigate = useNavigate();

  return (
    <ProceedingForm
      title={t('proceedings.form.title.edit')}
      schema={proceedingSchema}
      defaultValues={defaultProceedingValues}
      values={values}
      onSubmit={onSubmit}
      isError={isError}
      isSuccess={isSuccess}
      isSubmitting={isPending}
      onCancel={() => navigate({ to: '..' })}
      showWosFields={false}
      showScopusFields={proceeding.source.type === 'SCOPUS'}
    />
  );
}
